import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, firestore } from "../../../firebase";

const formatRole = (role) => {
  const normalizedRole = String(role || "").trim().toLowerCase();

  if (["seller", "facility_operator", "operator"].includes(normalizedRole)) {
    return "Facility Operator";
  }

  return role || "-";
};

const formatCreatedAt = (createdAt) => {
  if (!createdAt) return "-";
  const dateValue = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
  if (Number.isNaN(dateValue.getTime())) return "-";
  return dateValue.toLocaleDateString();
};

const FacilitySellerListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const statusParam = new URLSearchParams(location.search).get("status") || "all";

  const [tenantId, setTenantId] = useState("");
  const [currentFacilityId, setCurrentFacilityId] = useState("");
  const [sellers, setSellers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    statusParam.toLowerCase() === "active" ? "Active" : "All Status"
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [editData, setEditData] = useState({
    adminName: "",
    phoneNumber: "",
    email: "",
    status: "Active",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setStatusFilter(statusParam.toLowerCase() === "active" ? "Active" : "All Status");
  }, [statusParam]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user?.email) return;

      try {
        const profileQuery = query(
          collection(firestore, "UserProfileMaster"),
          where("email", "==", user.email)
        );
        const profileSnapshot = await getDocs(profileQuery);

        if (!profileSnapshot.empty) {
          const profileDoc = profileSnapshot.docs[0];
          const profileData = profileDoc.data();
          setTenantId(profileData.tenantId || "");
          setCurrentFacilityId(profileDoc.id);
        }
      } catch (error) {
        console.error("Error loading facility admin profile:", error);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!tenantId) {
      setSellers([]);
      return;
    }

    const usersQuery = query(
      collection(firestore, "UserProfileMaster"),
      where("tenantId", "==", tenantId)
    );

    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const scopedSellers = snapshot.docs
        .map((entry) => ({ id: entry.id, ...entry.data() }))
        .filter((user) => formatRole(user.role) === "Facility Operator" && user.facilityId === currentFacilityId);

      setSellers(scopedSellers);
    });

    return () => unsubscribe();
  }, [currentFacilityId, tenantId]);

  const filteredSellers = useMemo(() => {
    return sellers.filter((seller) => {
      const sellerName = (seller.adminName || seller.displayName || "").toLowerCase();
      const sellerEmail = (seller.email || "").toLowerCase();
      const matchesSearch =
        sellerName.includes(searchQuery.toLowerCase()) ||
        sellerEmail.includes(searchQuery.toLowerCase());
      const sellerStatus = seller.status || "Active";
      const matchesStatus = statusFilter === "All Status" || sellerStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, sellers, statusFilter]);

  const pageTitle = statusFilter === "Active" ? "Active Facility Operators" : "All Facility Operators";

  const openEditDialog = (seller) => {
    setSelectedSeller(seller);
    setEditData({
      adminName: seller.adminName || seller.displayName || "",
      phoneNumber: seller.phoneNumber || "",
      email: seller.email || "",
      status: seller.status || "Active",
    });
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    if (savingEdit) return;
    setEditDialogOpen(false);
    setSelectedSeller(null);
  };

  const handleEditFieldChange = (field) => (event) => {
    setEditData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveEdit = async () => {
    if (!selectedSeller?.id) return;

    if (!editData.adminName?.trim() || !editData.phoneNumber?.trim() || !editData.email?.trim()) {
      setSnackbar({
        open: true,
        message: "Name, phone number and email are required",
        severity: "error",
      });
      return;
    }

    setSavingEdit(true);
    try {
      await updateDoc(doc(firestore, "UserProfileMaster", selectedSeller.id), {
        adminName: editData.adminName.trim(),
        displayName: editData.adminName.trim(),
        phoneNumber: editData.phoneNumber.trim(),
        email: editData.email.trim().toLowerCase(),
        status: editData.status,
        isActive: editData.status === "Active",
        updatedAt: new Date(),
      });

      setSnackbar({
        open: true,
        message: "Facility operator updated successfully",
        severity: "success",
      });
      closeEditDialog();
    } catch (error) {
      console.error("Error updating seller:", error);
      setSnackbar({
        open: true,
        message: "Failed to update facility operator. Please try again.",
        severity: "error",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeleteDialog = (seller) => {
    setSelectedSeller(seller);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedSeller(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedSeller?.id) return;

    try {
      await deleteDoc(doc(firestore, "UserProfileMaster", selectedSeller.id));
      setSnackbar({
        open: true,
        message: "Facility operator deleted successfully",
        severity: "success",
      });
      closeDeleteDialog();
    } catch (error) {
      console.error("Error deleting seller:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete facility operator. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ bgcolor: "#f5f7fb", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1f2a44" }}>
              {pageTitle}
            </Typography>
            
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/facility-admin")}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Back to Facility Admin
          </Button>
        </Box>

        <Paper sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2, bgcolor: "#f8f9fb" },
                }}
              />
              <FormControl fullWidth>
                <Select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  sx={{ borderRadius: 2, bgcolor: "#f8f9fb" }}
                >
                  <MenuItem value="All Status">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ "& th": { color: "#6b778c", fontWeight: 700 } }}>
                    <TableCell>NAME</TableCell>
                    <TableCell>ROLE</TableCell>
                    <TableCell>PHONE</TableCell>
                    <TableCell>STATUS</TableCell>
                    <TableCell>JOINED</TableCell>
                    <TableCell align="center">ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSellers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No facility operators found for this filter.
                      </TableCell>
                    </TableRow>
                  )}

                  {filteredSellers.map((seller) => (
                    <TableRow key={seller.id} sx={{ "&:hover": { bgcolor: "#fafbff" } }}>
                      <TableCell>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: "#1f2a44" }}>
                            {seller.adminName || seller.displayName || "-"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#6b778c" }}>
                            {seller.email || "-"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatRole(seller.role)}</TableCell>
                      <TableCell>{seller.phoneNumber || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={seller.status || "Active"}
                          sx={{
                            bgcolor: (seller.status || "Active") === "Active" ? "#e8f5e9" : "#f5f5f5",
                            color: (seller.status || "Active") === "Active" ? "#2e7d32" : "#616161",
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>{formatCreatedAt(seller.createdAt)}</TableCell>
                      <TableCell align="center">
                        <IconButton color="primary" size="small" onClick={() => openEditDialog(seller)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton color="error" size="small" onClick={() => openDeleteDialog(seller)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>

        <Dialog open={editDialogOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
          <DialogTitle>Edit Facility Operator</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <TextField
                label="Facility Operator Name"
                required
                fullWidth
                value={editData.adminName}
                onChange={handleEditFieldChange("adminName")}
              />
              <TextField
                label="Phone No"
                required
                fullWidth
                value={editData.phoneNumber}
                onChange={handleEditFieldChange("phoneNumber")}
              />
              <TextField
                label="Email"
                type="email"
                required
                fullWidth
                value={editData.email}
                onChange={handleEditFieldChange("email")}
              />
              <FormControl fullWidth>
                <Select value={editData.status} onChange={handleEditFieldChange("status")}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeEditDialog} disabled={savingEdit}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit} sx={{ textTransform: "none", borderRadius: 2 }}>
              {savingEdit ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Facility Operator</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete {selectedSeller?.adminName || selectedSeller?.displayName || "this facility operator"}?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDeleteDialog}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacilitySellerListPage;